
import format from 'date-fns/format';
import addMonths from 'date-fns/addMonths';
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get, isUndefined } from 'lodash';

import {
  ISessionKey,
  ITrustYouReview,
} from '@share/common-types';
import {
  CONDO_SESSION_KEY,
  Routes,
  SESSION_EXPIRED_STATUS,
  SESSION_SEARCH_EXPIRED_MESSAGE,
  Urls,
} from '@share/constants';
import { getDateCheckInOut, getSelectedCurrency, getSessionObj, getTimeout, UrlUtils } from '@share/utils';
import { getHeaders, axiosInstance } from '@share/utils';
import { AppThunk } from '@share/utils';
import { SERVER_ERROR_STATUSES } from '@share/constants';

import { events, getCondoSearchBodyDetails, getEndDateNextMonth } from '@share/utils';
import {
  ICondoDetails,
  ICondoCheckinStatuses,
  CustomEvents,
  BookingErrorsEnum
} from '@share/common-types';

import { trustYouReviewActions } from './trust-you-review';
import { updateUnits, getUnitsDetails } from './condo-unit-search';
import { condosActions } from './condo-search';
import { NULL_VALUE } from '@constants';

export interface ICondoDetailsState {
  condo: ICondoDetails | null | undefined;
  loading: boolean;
  error: string;
  statuses: ICondoCheckinStatuses | null | undefined;
  isServerError: boolean;
  isCheckinStatusLoading: boolean;
  lastCheckinEndDate: string;
  loadingUnit: boolean;
}

const initialState: ICondoDetailsState = {
  condo: null,
  loading: true,
  error: '',
  statuses: NULL_VALUE,
  isServerError: false,
  isCheckinStatusLoading: false,
  lastCheckinEndDate: '',
  loadingUnit: false,
};

const ZERO = 0;
const one = 1;
const two = 2;
const emptyDatePartDays = 'T00:00:00.000Z';
const emptyDatePart = '-01T00:00:00.000Z';

const condoDetailsSlice = createSlice({
  name: 'condo-details',
  initialState,
  reducers: {
    setLoading: (state: ICondoDetailsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setLastCheckinEndDate: (state: ICondoDetailsState, { payload }: PayloadAction<string>) => {
      state.lastCheckinEndDate = payload;

      setTimeout(() => {
        events.emit(CustomEvents.LastCheckinEndDate, payload);
      });
    },
    setLoadingUnit: (state: ICondoDetailsState, { payload }: PayloadAction<boolean>) => {
      state.loadingUnit = payload;
    },
    setError: (state: ICondoDetailsState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setCondoDetails: (state: ICondoDetailsState, { payload }: PayloadAction<ICondoDetails | null | undefined>) => {
      state.condo = payload;
    },
    setUpdateUnitsType: (state: ICondoDetailsState, { payload }: PayloadAction<ICondoDetails | null | undefined>) => {
      state.condo = payload;
    },
    setIsCheckinStatusLoading: (state: ICondoDetailsState, { payload }: PayloadAction<boolean>) => {
      state.isCheckinStatusLoading = payload;
      events.emit(CustomEvents.CheckinStatusesLoading, payload);
    },
    setIsServerError: (state: ICondoDetailsState, { payload }: PayloadAction<boolean>) => {
      state.isServerError = payload;
    },
    setStatuses: (state: ICondoDetailsState, { payload }: PayloadAction<ICondoCheckinStatuses | null | undefined>) => {
      if (!payload) {
        state.statuses = payload;
      } else {
        if (!state.statuses) {
          state.statuses = payload;
        } else {
          const { availableDays, unavailableDays } = payload;

          Object.keys(availableDays).forEach((year) => {
            if (state.statuses && !state.statuses?.availableDays[+year]) {
              state.statuses.availableDays[+year] = availableDays[+year];
            } else {
              Object.keys(availableDays[+year]).forEach((month) => {
                if (state.statuses && !state.statuses?.availableDays[+year][+month]) {
                  state.statuses.availableDays[+year][+month] = availableDays[+year][+month];
                }
              });
            }
          });

          Object.keys(unavailableDays).forEach((year) => {
            if (state.statuses && !state.statuses?.unavailableDays[+year]) {
              state.statuses.unavailableDays[+year] = unavailableDays[+year];
            } else {
              Object.keys(unavailableDays[+year]).forEach((month) => {
                if (state.statuses && !state.statuses?.unavailableDays[+year][+month]) {
                  state.statuses.unavailableDays[+year][+month] = unavailableDays[+year][+month];
                }
              });
            }
          });
        }
      }

      setTimeout(() => {
        events.emit(CustomEvents.CheckinStatusesUpdated);
      });
    },
    resetState: () => {
      return initialState;
    },
  },
});

export const condoDetailsActions = condoDetailsSlice.actions;

export const condoDetailsReducer = condoDetailsSlice.reducer;

export const updateSavedPropertyDetailsCondos = (propertyId: number, added: boolean): AppThunk => {
  return async (dispatch, getState) => {

    const { condoDetailsStore } = getState();
    const { condo } = condoDetailsStore;
     
    if (condo) {
      const cloned = JSON.parse(JSON.stringify(condo));
      cloned.condoDetails.savedProperty = cloned.condoDetails.id == propertyId ? added : cloned.savedProperty;
      dispatch(condoDetailsActions.setCondoDetails(cloned));
    }
  };
};

export const getCondoDetails = (
  condoId: number,
  sessionKey: ISessionKey | undefined,
  isMobile = false,
  condoRequest?: any,
  dealId?: number,
  quote?: string,
  forceSessionKey?: boolean,
  onSuccess?: (details: any) => void,
  isUnitLoading?: boolean
): AppThunk => {
  return async (dispatch, getState) => {
    if (!isUnitLoading) {
      dispatch(condoDetailsActions.setLoading(true));
    } else {
      dispatch(condoDetailsActions.setLoadingUnit(true));
    }
    dispatch(condoDetailsActions.setStatuses(NULL_VALUE));
    dispatch(condoDetailsActions.setIsServerError(false));
    
    const { loginStore, marginatorStore } = getState();
    const { account } = loginStore;
    const { commission } = marginatorStore;

    try {  
      const percentage = marginatorStore.percentage || 0;

      const currency = getSelectedCurrency(account);
  
      const res = await axiosInstance.post(
          Urls.CondoDetails,
          { 
            condoId,
            sessionKey,
            searchType: condoRequest?.requestType,
            ...getCondoSearchBodyDetails(condoRequest),
            dealId,
            quote,
            currency,
            marginator: { percentage, commission },
          },
          { ...getHeaders() },
        );

      if (!isUnitLoading) {
        dispatch(condoDetailsActions.setLoading(false));
      } else {
        dispatch(condoDetailsActions.setLoadingUnit(false));
      }

      dispatch(condoDetailsActions.setCondoDetails(res.data));

      // @ts-ignore
      updateUnits(res.data.sessionKey?.expireDate, dispatch, getState);

      onSuccess?.(res.data?.condoDetails);

      if (res.data?.sessionKey?.isInvalid) {
        dispatch(getUnitsDetails(condoId));
      } else {
        if (forceSessionKey) {
          dispatch(condosActions.setCondoSessionKey(res.data.sessionKey));
        } else {
          const values = UrlUtils.getValues();
          const session = getSessionObj(values[CONDO_SESSION_KEY] as ISessionKey, values[CONDO_SESSION_KEY] as ISessionKey);
          const timeoutSessionStoreExpired = getTimeout(session?.expireDate) <= 0;
  
          if (isUndefined(session) || (!!quote && timeoutSessionStoreExpired)) {
            dispatch(condosActions.setCondoSessionKey(res.data.sessionKey));
          }
        }
      }

      setTimeout(() => {
        const { unitsSearchStore } = getState();

        if (unitsSearchStore.startDate && unitsSearchStore.endDate) {
          const today = new Date();
          const start = getDateCheckInOut(unitsSearchStore.startDate);
          const startMonth = new Date(`${start ? format(start, 'yyyy-MM') : ''}${emptyDatePart}`);
          const end = subDays(addMonths(startMonth, two), one);
          const isSameMonth = today.getMonth() === start?.getMonth();
          const isNextMonth = today.getMonth() === (start?.getMonth() || 0)  - one;
          const tomorrow = addDays(today, one);
          let startDate;
          let endDate;

          if (isSameMonth || isNextMonth || isMobile) {
            startDate = `${format(tomorrow, 'yyyy-MM-dd')}${emptyDatePartDays}`;
          } else {
            startDate = `${start ? format(start, 'yyyy-MM') : ''}${emptyDatePart}`;
          }

          if (isMobile) {
            endDate = `${format(addMonths(end, one), 'yyyy-MM-dd')}${emptyDatePartDays}`;
          } else {
            endDate = `${format(end, 'yyyy-MM-dd')}${emptyDatePartDays}`;
          }

          dispatch(getCheckinStatus(startDate, endDate));
        } else {
          const currentDate = new Date();
          const currentDateFormat = `${format(currentDate, 'yyyy-MM-dd')}${emptyDatePartDays}`;
          const endDateNextMonthFormat = `${format(
            getEndDateNextMonth(),
            'yyyy-MM-dd',
          )}${emptyDatePartDays}`;

          dispatch(getCheckinStatus(currentDateFormat, endDateNextMonthFormat));
        }
      });
    } catch (error: any) {
      const errorObject = error?.response?.data;
      const errorCode = get(errorObject, 'code', error.toString());

      dispatch(condoDetailsActions.setError(errorCode));
      if (!isUnitLoading) {
        dispatch(condoDetailsActions.setLoading(false));
      } else {
        dispatch(condoDetailsActions.setLoadingUnit(false));
      }

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        error?.response?.data[ZERO].errorMessage === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        history.replaceState(null, NULL_VALUE, `/${account?.name}${Routes.CondoSearch}${location.search}`);
        location.reload();
      } else if (SERVER_ERROR_STATUSES.includes(error?.response?.status)) {
        dispatch(condoDetailsActions.setIsServerError(true));
      }

      if ([BookingErrorsEnum.RoomsUnavailable, BookingErrorsEnum.SoldOut].includes(errorCode)) {
        dispatch(condoDetailsActions.setError(errorCode));
      }
    }
  };
};

export const getCondoStaticDetails = (condoId: number, searchType: string, _skipRoomContent: boolean = false, onSuccess?: () => void): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(condoDetailsActions.setLoading(true));
    dispatch(condoDetailsActions.setStatuses(NULL_VALUE));
    dispatch(condoDetailsActions.setIsServerError(false));

    const { loginStore } = getState();
    const { account } = loginStore;
  
    try {
      const res = await axiosInstance.get(`/property/static/${condoId}`);

      if (res.data) {
        const checkInCheckOutInfo: any = {};
        if (res.data.details?.checkin?.specialInstructions) {
          checkInCheckOutInfo.arrivalCheckInSpecialInstructions = res.data.details?.checkin?.specialInstructions;
        }
        if (res.data.details?.checkin?.beginTime) {
          checkInCheckOutInfo.checkInTime = res.data.details?.checkin?.beginTime;
        }
        if (res.data.details?.checkin?.specialInstructions) {
          checkInCheckOutInfo.description = res.data.details?.checkin?.instructions;
        }
        if (res.data.details?.checkout?.time) {
          checkInCheckOutInfo.checkOutTime = res.data.details?.checkout?.time;
        }

        const descriptions = [];
        if (res.data.details?.descriptions?.propertyInformation) {
          descriptions.push(res.data.details?.descriptions?.propertyInformation);
        }
        if (res.data.details?.descriptions?.location) {
          descriptions.push(res.data.details?.descriptions?.location);
        }
        if (res.data.details?.descriptions?.other) {
          descriptions.push(res.data.details?.descriptions?.other);
        }
        
        const fees = [];
        if (res.data.details?.fees?.resort) {
          fees.push({infoMessage: res.data.details?.fees?.resort});
        }

        const mandatoryFees = [];
        if (res.data.details?.fees?.mandatory) {
          mandatoryFees.push({infoMessage: res.data.details?.fees?.mandatory});
        }
        
        const urgentInfo: any = {};
        if (res.data.details?.descriptions?.importantInformation) {
          urgentInfo.generalUrgentInfoMessages = res.data.details?.descriptions?.importantInformation;
        }

        const ageRestrictions = res.data.details?.policies?.policyRestrictions?.some((policy: string) => policy.startsWith('Minimum Age Checkin:'));
        const pets = res.data.details?.policies?.policyRestrictions?.some((policy: string) => policy.startsWith('Pets:'));
        const smoking = res.data.details?.policies?.policyRestrictions?.some((policy: string) => policy.startsWith('Smoking Restrictions:'));

        const details: any = {
          condoDetails: {
            activities: res.data.details?.activities,
            accessibilities: [],
            address: res.data.address,
            airports: res.data?.details?.airports,
            amenities: res.data.amenities?.length ? 
                            res.data.amenities.map((a: any) => ({
                              amenityId: a.id,
                              categoryName: a.categoryName,
                              name: a.amenityName,
                            })) : 
                            [],
            ageRestrictions: ageRestrictions ? [ageRestrictions] : [],
            checkInCheckOutInfo,
            city: res.data.cityName,
            country: res.data.countryName,
            descriptions,
            facilities: res.data.details?.facilities || [],
            fees,
            fullAddress: `${res.data.address}${res.data.cityName ? ', ' : ''}${res.data.cityName}${res.data.stateCode ? ', ' : ''}${res.data.stateCode}${res.data.countryName ? ', ' : ''}${res.data.countryName}`,
            highlights: res.data.highlights,
            housekeepingFees: [],
            id: res.data.propertyId,
            location: {
              latitude: res.data.lat,
              longitude: res.data.lng,
            },
            mandatoryFees,
            media: res.data.details?.images,
            pets: pets ? [pets] : [],
            rating: {
              reviewsCount: res.data.reviewsCount,
              value: res.data.reviews,
              trustYouId: '',
            },
            smoking: smoking ? [smoking] : [],
            surroundings: [],
            thumb: res.data.thumb,
            title: res.data.name,
            urgentInfo,

            // TODO: read rooms
            units: [],

            withoutTaxes: false
          },

          searchType,
          sessionKey: null,
        };


        dispatch(condoDetailsActions.setCondoDetails(details));
  
        onSuccess?.();
      }
      
      dispatch(condoDetailsActions.setLoading(false));
    } catch (error: any) {
      const errorObject = error?.response?.data;
      const errorCode = get(errorObject, 'code', error.toString());

      dispatch(condoDetailsActions.setError(errorCode));
      dispatch(condoDetailsActions.setLoading(false));

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        error?.response?.data[ZERO].errorMessage === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        history.replaceState(null, NULL_VALUE, `/${account?.name}${Routes.CondoSearch}${location.search}`);
        location.reload();
      } else if (SERVER_ERROR_STATUSES.includes(error?.response?.status)) {
        dispatch(condoDetailsActions.setIsServerError(true));
      }

      if ([BookingErrorsEnum.RoomsUnavailable, BookingErrorsEnum.SoldOut].includes(errorCode)) {
        dispatch(condoDetailsActions.setError(errorCode));
      }
    }
  };
};

export const getTrustYouReview = (trustYouId: string): AppThunk => {
  return async (dispatch, getState) => {
    try {
      const { loginStore } = getState();
      const { user } = loginStore;
      const res = await axiosInstance.get(`/condos/${trustYouId}/review`, {
        ...getHeaders(user?.accessToken),
      });

      dispatch(trustYouReviewActions.setReviewModule(res.data?.response as ITrustYouReview));
    } catch (error) {
      console.error(error);
    }
  };
};

export const getCheckinStatus = (startDate: string, endDate: string): AppThunk => {
  return async (dispatch, getState) => {
    try {
      dispatch(condoDetailsActions.setLastCheckinEndDate(endDate));
      dispatch(condoDetailsActions.setIsCheckinStatusLoading(true));
      const { condoDetailsStore, unitsSearchStore, loginStore } = getState();
      const { user } = loginStore;

      const { data } = await axiosInstance.post(
        Urls.CheckinStatus,
        {
          condoId: condoDetailsStore.condo?.condoDetails?.id,
          dateRange: {
            from: startDate,
            to: endDate,
          },
          adultsCount: unitsSearchStore.guests?.adultsCount,
          childrenCount: unitsSearchStore.guests?.kidsCount,
          bedroomsCount: unitsSearchStore.guests?.bedroomsCount,
        },
        {
          ...getHeaders(user?.accessToken),
        },
      );

      dispatch(condoDetailsActions.setStatuses(data));
      dispatch(condoDetailsActions.setIsCheckinStatusLoading(false));
    } catch (error) {
      dispatch(condoDetailsActions.setIsCheckinStatusLoading(false));
      console.error(error);
    }
  };
};
